import type { IconName } from "../../atoms/icons";
import type { TypographyVariantProps } from "../../atoms/typography";
import type { BannerVariant } from "./types";

// ─── Container ───────────────────────────────────────────────────────────────

/**
 * Tailwind classes for the root container (background + border-color) per variant.
 * The 1px border (`border-1`) is always applied by the component itself.
 */
export const bannerContainerClasses: Record<BannerVariant, string> = {
  default: "bg-luce-neutral-background-default border-luce-neutral-border-highEmphasis",
  info: "bg-luce-system-info-surface border-luce-system-info-border",
  success: "bg-luce-system-success-surface border-luce-system-success-border",
  danger: "bg-luce-system-danger-surface border-luce-system-danger-border",
  warning: "bg-luce-system-warning-surface border-luce-system-warning-border",
  neutral: "bg-luce-neutral-background-intermediate border-luce-neutral-border-highEmphasis",
};

// ─── Icon ─────────────────────────────────────────────────────────────────────

/** Phosphor icon name to display per variant. */
export const bannerIconName: Record<BannerVariant, IconName> = {
  default: "Info",
  info: "Info",
  success: "CheckCircle",
  danger: "WarningCircle",
  warning: "WarningCircle",
  neutral: "WarningCircle",
};

/**
 * Tailwind color class applied to the icon per variant.
 * Passed directly to `Icon`'s `className` prop.
 */
export const bannerIconClassName: Record<BannerVariant, string> = {
  default: "text-luce-neutral-foreground-intermediate",
  info: "text-luce-system-info-onSurface",
  success: "text-luce-system-success-onSurface",
  danger: "text-luce-system-danger-onSurface",
  warning: "text-luce-system-warning-onSurface",
  neutral: "text-luce-neutral-foreground-intermediate",
};

// ─── Typography ───────────────────────────────────────────────────────────────

type TypographyColor = NonNullable<TypographyVariantProps["color"]>;

/** Typography `color` token for the title per variant. */
export const bannerTitleColor: Record<BannerVariant, TypographyColor> = {
  default: "neutral-foreground-default",
  info: "system-info-on-surface",
  success: "system-success-on-surface",
  danger: "system-danger-on-surface",
  warning: "system-warning-on-surface",
  neutral: "neutral-foreground-default",
};

/** Typography `color` token for the description per variant. */
export const bannerDescriptionColor: Record<BannerVariant, TypographyColor> = {
  default: "neutral-foreground-intermediate",
  info: "system-info-on-surface",
  success: "system-success-on-surface",
  danger: "system-danger-on-surface",
  warning: "system-warning-on-surface",
  neutral: "neutral-foreground-intermediate",
};

/**
 * Typography `color` token for a message-only Banner's text (Figma's
 * Title=False), which Figma binds apart from the title and description
 */
export const bannerMessageColor: Record<BannerVariant, TypographyColor> = {
  default: "neutral-foreground-default",
  info: "system-info-on-surface",
  success: "system-success-on-surface",
  danger: "system-danger-on-surface",
  warning: "system-warning-on-surface",
  neutral: "neutral-foreground-intermediate",
};
